// Enhanced Mining Data Hook with Persistence and Retry Logic

import { useEffect, useState, useCallback, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import { useToast } from '../context/ToastContext';
import { miningServices } from '../services';
import { persistenceService } from '../services/persistence';

interface MiningDataState {
  isInitialized: boolean;
  isLoading: boolean;
  isFetching: boolean;
  lastFetchTime: number;
  error: string | null;
  retryCount: number;
  nextRetryTime: number;
}

export const useMiningDataPersistent = () => {
  const { state, dispatch } = useAppContext();
  const { showError, showWarning, showSuccess, showInfo } = useToast();
  const [dataState, setDataState] = useState<MiningDataState>({
    isInitialized: false,
    isLoading: true,
    isFetching: false,
    lastFetchTime: 0,
    error: null,
    retryCount: 0,
    nextRetryTime: 0
  });

  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Load cached data immediately on mount
  const loadCachedData = useCallback(() => {
    console.log('📦 Loading cached data...');
    
    const cachedStats = persistenceService.getDashboardStats();
    const cachedMiners = persistenceService.getMinersData();
    const cachedCrypto = persistenceService.getCryptoProgress();
    const cachedHashrate = persistenceService.getAverageHashrate();
    
    if (cachedStats) {
      dispatch({ type: 'SET_DASHBOARD_STATS', payload: cachedStats });
      console.log('✅ Loaded cached dashboard stats');
    }
    
    if (cachedMiners) {
      dispatch({ type: 'SET_MINERS', payload: cachedMiners });
      console.log('✅ Loaded cached miners data');
    }
    
    // Set as initialized if we have cached data
    const hasAnyCache = cachedStats || cachedMiners || cachedCrypto || cachedHashrate;
    if (hasAnyCache) {
      setDataState(prev => ({
        ...prev,
        isInitialized: true,
        isLoading: false,
        lastFetchTime: persistenceService.getFetchStatus().lastSuccessfulFetch
      }));
      console.log('✅ Initialized with cached data');
    }
    
    return hasAnyCache;
  }, [dispatch]);

  // Centralized fetch function with improved error handling and data preservation
  const fetchAllData = useCallback(async (isRetry = false) => {
    // Check if we should retry
    if (isRetry && !persistenceService.shouldRetry()) {
      const status = persistenceService.getFetchStatus();
      setDataState(prev => ({
        ...prev,
        nextRetryTime: status.nextRetryTime,
        retryCount: status.failedAttempts
      }));
      return false;
    }

    console.log(`🔄 ${isRetry ? 'Retrying' : 'Starting'} data fetch...`);
    
    setDataState(prev => ({
      ...prev,
      isFetching: true,
      error: null
    }));

    // Store current data before attempting fetch (for rollback if needed)
    const currentDashboardStats = state.dashboardStats;
    const currentMiners = state.miners;

    try {
      // Fetch data sequentially with individual error handling
      let dashboardStats = currentDashboardStats;
      let minersData = currentMiners;
      let cryptoProgress = null;
      let averageHashrate = null;
      
      let hasAnySuccess = false;
      let lastError = null;

      // Try to fetch dashboard stats
      try {
        console.log('📊 Fetching dashboard stats...');
        dashboardStats = await miningServices.getDashboardStats();
        hasAnySuccess = true;
        console.log('✅ Dashboard stats fetched successfully');
      } catch (error) {
        console.warn('⚠️ Dashboard stats fetch failed, keeping existing data:', error);
        lastError = error;
        // Keep existing data, don't throw
      }
      
      // Small delay between requests
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Try to fetch miners data
      try {
        console.log('⛏️ Fetching miners data...');
        minersData = await miningServices.getAllWorkers();
        hasAnySuccess = true;
        console.log('✅ Miners data fetched successfully');
      } catch (error) {
        console.warn('⚠️ Miners data fetch failed, keeping existing data:', error);
        lastError = error;
        // Keep existing data, don't throw
      }
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Try to fetch crypto progress (optional data)
      try {
        console.log('💎 Fetching crypto progress...');
        cryptoProgress = await miningServices.getCryptoProgress();
        hasAnySuccess = true;
        console.log('✅ Crypto progress fetched successfully');
      } catch (error) {
        console.warn('⚠️ Crypto progress fetch failed:', error);
        lastError = error;
        // This is optional, continue
      }
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Try to fetch average hashrate (optional data)
      try {
        console.log('📈 Fetching average hashrate...');
        averageHashrate = await miningServices.getAverageHashrate();
        hasAnySuccess = true;
        console.log('✅ Average hashrate fetched successfully');
      } catch (error) {
        console.warn('⚠️ Average hashrate fetch failed:', error);
        lastError = error;
        // This is optional, continue
      }

      // If we got at least some data, consider it a partial success
      if (hasAnySuccess) {
        // Update Redux state with whatever data we got
        if (dashboardStats) {
          dispatch({ type: 'SET_DASHBOARD_STATS', payload: dashboardStats });
          persistenceService.setDashboardStats(dashboardStats);
        }
        
        if (minersData) {
          dispatch({ type: 'SET_MINERS', payload: minersData });
          persistenceService.setMinersData(minersData);
        }
        
        // Clear errors for successful parts
        dispatch({ type: 'SET_ERROR', payload: { key: 'dashboard', value: null } });
        dispatch({ type: 'SET_ERROR', payload: { key: 'miners', value: null } });

        // Cache optional data if available
        if (cryptoProgress) {
          persistenceService.setCryptoProgress(cryptoProgress);
        }
        if (averageHashrate) {
          persistenceService.setAverageHashrate(averageHashrate);
        }

        // Mark as successful
        persistenceService.markFetchSuccess();

        setDataState(prev => ({
          ...prev,
          isInitialized: true,
          isLoading: false,
          isFetching: false,
          lastFetchTime: Date.now(),
          error: null,
          retryCount: 0,
          nextRetryTime: 0
        }));

        // Show success notification only on recovery from error state
        if (isRetry) {
          showSuccess(
            'Connection Restored',
            'Mining data has been successfully updated'
          );
        }

        console.log('✅ Data fetch completed with at least partial success');
        return true;
      } else {
        // No data was fetched successfully, throw the last error
        throw lastError || new Error('All fetch attempts failed');
      }

    } catch (error) {
      console.error('❌ Complete fetch failure:', error);
      
      // Mark as failed and get retry info
      persistenceService.markFetchFailure();
      const status = persistenceService.getFetchStatus();
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch mining data';
      
      setDataState(prev => ({
        ...prev,
        isFetching: false,
        error: errorMessage,
        retryCount: status.failedAttempts,
        nextRetryTime: status.nextRetryTime
      }));

      // CRITICAL: Preserve existing data in Redux during errors
      // Always ensure we have data from cache if Redux state is empty
      const cachedStats = persistenceService.getDashboardStats();
      const cachedMiners = persistenceService.getMinersData();
      
      if (!state.dashboardStats && cachedStats) {
        dispatch({ type: 'SET_DASHBOARD_STATS', payload: cachedStats });
        console.log('📦 Restored dashboard stats from cache during error');
      }
      
      if ((!state.miners || state.miners.length === 0) && cachedMiners) {
        dispatch({ type: 'SET_MINERS', payload: cachedMiners });
        console.log('📦 Restored miners data from cache during error');
      }

      // Set error state but don't clear data
      dispatch({ type: 'SET_ERROR', payload: { key: 'dashboard', value: errorMessage } });
      dispatch({ type: 'SET_ERROR', payload: { key: 'miners', value: errorMessage } });

      console.log('📦 Data preservation completed during error state');

      // Show appropriate toast notifications based on error type and attempt count
      if (status.failedAttempts === 1) {
        // First failure - show warning
        if (errorMessage.includes('Circuit breaker')) {
          showWarning(
            'Service Temporarily Unavailable',
            'Mining API is experiencing issues. Using cached data while we retry.'
          );
        } else if (errorMessage.includes('timeout')) {
          showWarning(
            'Connection Timeout',
            'Request timed out. Retrying with cached data visible.'
          );
        } else {
          showWarning(
            'Connection Issue',
            'Unable to fetch fresh data. Using cached data while we retry.'
          );
        }
      } else if (status.failedAttempts >= 3) {
        // Max attempts reached - show error
        showError(
          'Connection Failed',
          'Unable to connect to mining services after multiple attempts. Displaying cached data only.'
        );
      }

      // Schedule retry if we haven't exceeded max attempts
      if (status.failedAttempts < 3) {
        const retryDelay = persistenceService.calculateRetryDelay(status.failedAttempts);
        console.log(`⏰ Scheduling retry in ${retryDelay}ms`);
        
        // Show info about retry for subsequent attempts
        if (status.failedAttempts > 1) {
          showInfo(
            'Retrying Connection',
            `Attempting to reconnect in ${Math.ceil(retryDelay / 1000)} seconds...`
          );
        }
        
        retryTimeoutRef.current = setTimeout(() => {
          fetchAllData(true);
        }, retryDelay);
      } else {
        console.log('🛑 Max retry attempts reached, keeping cached data');
      }

      return false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, state.dashboardStats, state.miners]);

  // Manual refresh function
  const refreshData = useCallback(async () => {
    // Clear any pending retries
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    
    // Reset fetch status to allow immediate retry
    persistenceService.setFetchStatus({
      failedAttempts: 0,
      nextRetryTime: 0
    });
    
    // Show info notification for manual refresh
    showInfo(
      'Refreshing Data',
      'Fetching latest mining information...'
    );
    
    const success = await fetchAllData(false);
    
    if (success) {
      showSuccess(
        'Data Updated',
        'Mining information has been refreshed successfully'
      );
    }
    
    return success;
  }, [fetchAllData, showInfo, showSuccess]);

  // Initialize data on mount
  useEffect(() => {
    console.log('🚀 Initializing mining data with persistence...');
    
    // Load cached data first
    const hasCachedData = loadCachedData();
    
    // Always fetch fresh data, but don't show loading if we have cache
    if (!hasCachedData) {
      setDataState(prev => ({ ...prev, isLoading: true }));
    }
    
    // Check if we need to refresh
    if (persistenceService.needsRefresh()) {
      console.log('🔄 Cache needs refresh, fetching fresh data...');
      fetchAllData(false);
    } else {
      console.log('✅ Cache is fresh, no fetch needed');
      setDataState(prev => ({
        ...prev,
        isInitialized: true,
        isLoading: false
      }));
    }

    // Cleanup timeouts on unmount
    return () => {
      const retryTimeout = retryTimeoutRef.current;
      
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
    };
  }, [loadCachedData, fetchAllData]);

  // Auto-refresh with controlled intervals
  useEffect(() => {
    if (!dataState.isInitialized || !state.settings.autoRefresh || dataState.isFetching) {
      return;
    }

    console.log('⏰ Setting up controlled auto-refresh...');
    
    // Only refresh if cache is expired and we're not in retry backoff
    const refreshInterval = setInterval(() => {
      if (persistenceService.needsRefresh() && persistenceService.shouldRetry()) {
        console.log('🔄 Auto-refresh triggered');
        fetchAllData(false);
      }
    }, 30000); // Check every 30 seconds

    return () => {
      clearInterval(refreshInterval);
      console.log('🛑 Auto-refresh cleared');
    };
  }, [dataState.isInitialized, dataState.isFetching, state.settings.autoRefresh, fetchAllData]);

  // Calculate derived statistics
  const derivedStats = {
    totalHashrateFormatted: state.dashboardStats 
      ? `${(state.dashboardStats.totalHashrate / 1000000000).toFixed(2)} GH/s`
      : '---',
    
    averageHashratePerWorker: state.dashboardStats && state.dashboardStats.totalWorkers > 0
      ? state.dashboardStats.totalHashrate / state.dashboardStats.totalWorkers
      : 0,
    
    onlineMinersCount: state.miners.filter(m => m.status === 'online').length,
    offlineMinersCount: state.miners.filter(m => m.status === 'offline').length,
    errorMinersCount: state.miners.filter(m => m.status === 'error').length,
    
    sha256Hashrate: state.miners
      .filter(m => m.algorithm === 'SHA256' && m.status === 'online')
      .reduce((sum, miner) => sum + miner.hashrate, 0),
    
    scryptHashrate: state.miners
      .filter(m => m.algorithm === 'Scrypt' && m.status === 'online')
      .reduce((sum, miner) => sum + miner.hashrate, 0),
    
    lastUpdateTime: dataState.lastFetchTime,
    
    // Retry information
    timeUntilRetry: Math.max(0, dataState.nextRetryTime - Date.now())
  };

  return {
    // State from Redux
    ...state,
    
    // Local state
    ...dataState,
    derivedStats,
    
    // Actions
    refreshData,
    clearCache: persistenceService.clearAllCache.bind(persistenceService),
    
    // Status
    apiStatus: dataState.isInitialized ? 
      (dataState.error ? 'error' : 'connected') : 
      'connecting',
    
    // Cache information
    cacheInfo: persistenceService.getCacheInfo(),
    dataFreshness: persistenceService.getDataFreshness(),
    
    // Retry information
    canRetry: persistenceService.shouldRetry(),
    timeUntilRetry: Math.max(0, dataState.nextRetryTime - Date.now()),
    
    // Cached data getters
    getCachedCryptoProgress: persistenceService.getCryptoProgress.bind(persistenceService),
    getCachedAverageHashrate: persistenceService.getAverageHashrate.bind(persistenceService)
  };
};

export default useMiningDataPersistent;